import { RootActions, RootState } from "common/reducers";
import { receive } from "common/reducers/Utils";
import { Map, Record } from "immutable";
import {
  calcEstimateLineItemOptionTotals,
  estimateLineItemOptionFromJSON,
  EstimateLineItemOptionRecord,
} from "common/records/EstimateLineItemOption";
import {
  RECEIVE_ESTIMATE_LINE_ITEM_OPTION,
  DUPLICATE_ESTIMATE_LINE_ITEM_OPTION,
  UPDATE_ESTIMATE_LINE_ITEM_OPTION_TOTALS,
  DECREMENT_ESTIMATE_LINE_ITEM_OPTION_UNSAVED_ID,
  SET_ESTIMATE_LINE_ITEM_OPTION_BY_ID,
  EstimateLineItemOptionActions,
  COPY_ESTIMATE_LINE_ITEM_OPTION_TO_LAST_SAVED,
} from "common/reducers/estimateLineItemOption.actions";
import {
  estimateLineItemOption,
  unsavedId as estimateLineItemOptionUnsavedIdSelector,
} from "common/selectors/estimateLineItemOption.selectors";
import { estimateLineItem } from "common/selectors/estimateLineItem.selectors";
import { estimate } from "common/selectors/estimate.selectors";
import { estimateGroup } from "common/selectors/estimateGroup.selectors";

export const EstimateLineItemOptionStateRecord = Record({
  byId: Map<number, EstimateLineItemOptionRecord>(),
  lastSavedById: Map<number, EstimateLineItemOptionRecord>(), // What's saved on the SERVER (for dirty tracking)
  editingSnapshotById: Map<number, EstimateLineItemOptionRecord>(), // Snapshot for line item rollback
  unsavedId: -1,
});

export const initialState = EstimateLineItemOptionStateRecord();

export const model = "estimateLineItemOptions";

export const estimateLineItemOptionsReducer = (state: RootState, action: RootActions): RootState => {
  if (state && !state.get(model)) {
    return state.set(model, initialState);
  }

  switch (action.type) {
    case RECEIVE_ESTIMATE_LINE_ITEM_OPTION: {
      const option = action.payload.estimateLineItemOption;

      const optionRecord = estimateLineItemOptionFromJSON(option);
      state = receive(state, model, optionRecord);

      // Copy to lastSavedById - represents what's saved on the SERVER
      state = state.setIn([model, "lastSavedById", option.id], optionRecord);

      return state;
    }

    case DUPLICATE_ESTIMATE_LINE_ITEM_OPTION: {
      const { sourceOptionId, targetLineItemId } = action.payload;

      const originalOption = estimateLineItemOption(state, { estimateLineItemOptionId: sourceOptionId });
      if (!originalOption || originalOption._destroy) {
        return state;
      }

      const newOptionId = estimateLineItemOptionUnsavedIdSelector(state);
      state = estimateLineItemOptionsReducer(state, EstimateLineItemOptionActions.decrementUnsavedId());

      const duplicatedOptionData = {
        ...originalOption.toJS(),
        id: newOptionId,
        estimate_line_item_id: targetLineItemId,
      };
      state = estimateLineItemOptionsReducer(
        state,
        EstimateLineItemOptionActions.setEstimateLineItemOptionById(newOptionId, duplicatedOptionData),
      );

      const targetLineItemRecord = estimateLineItem(state, { estimateLineItemId: targetLineItemId });
      if (targetLineItemRecord) {
        const updatedOptionIds = targetLineItemRecord.option_ids.push(newOptionId);
        state = state.setIn(["estimateLineItems", "byId", targetLineItemId, "option_ids"], updatedOptionIds);
      }

      return state;
    }

    case UPDATE_ESTIMATE_LINE_ITEM_OPTION_TOTALS: {
      const { estimateLineItemOptionId } = action.payload;
      const lineItemOption = estimateLineItemOption(state, { estimateLineItemOptionId });
      const lineItem = estimateLineItem(state, { estimateLineItemId: lineItemOption?.estimate_line_item_id });
      const estimateGroupRecord = estimateGroup(state, { estimateGroupId: lineItem?.estimate_group_id });
      const estimateRecord = estimate(state, { estimateId: estimateGroupRecord?.estimate_id });

      if (!lineItemOption || !lineItem || !estimateRecord) {
        console.error("Missing required data in redux to calculate estimate line item option totals", {
          estimateLineItemOptionId,
          estimateLineItemOption: lineItemOption?.toJS(),
          estimateLineItem: lineItem?.toJS(),
          estimateGroup: estimateGroupRecord?.toJS(),
          estimate: estimateRecord?.toJS(),
        });
        return state;
      }

      const totals = calcEstimateLineItemOptionTotals(lineItemOption, lineItem, estimateRecord);
      return state.setIn([model, "byId", estimateLineItemOptionId], lineItemOption.merge(totals));
    }

    case DECREMENT_ESTIMATE_LINE_ITEM_OPTION_UNSAVED_ID: {
      const currentUnsavedId = state.getIn([model, "unsavedId"]) as number;
      return state.setIn([model, "unsavedId"], currentUnsavedId - 1);
    }

    case SET_ESTIMATE_LINE_ITEM_OPTION_BY_ID: {
      const { estimateLineItemOptionId, estimateLineItemOption: optionData } = action.payload;
      const optionRecord = estimateLineItemOptionFromJSON(optionData);
      return state.setIn([model, "byId", estimateLineItemOptionId], optionRecord);
    }

    case COPY_ESTIMATE_LINE_ITEM_OPTION_TO_LAST_SAVED: {
      const { estimateLineItemOptionId } = action.payload;
      const optionRecord = state.getIn([model, "byId", estimateLineItemOptionId]);
      if (!optionRecord) {
        return state;
      }
      return state.setIn([model, "lastSavedById", estimateLineItemOptionId], optionRecord);
    }

    default:
      return state;
  }
};
